import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ExperimentStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { ShortlistStore } from 'stores/shortlist-store';

import { HotelMapModel, MapMarker } from 'models/hotel/map-types';
import { PropertySearchListingViewModel } from 'models/hotel/search-types';

import { AirportMarker, PlaceMarker, PriceMarker, SoldOutMarker } from 'uitk-react-map';

import * as MarkerClusterer from 'components/common/map/markers/markerclusterer.js';

const SOLD_OUT_ICON = 'SOLD_OUT';
const LANDMARK = 'LANDMARK';
const AIRPORT = 'AIRPORT';

export interface ClusteringMarkersProps {
  experiment?: ExperimentStore;
  google?: any;
  hotelIdFromMap?: number;
  map?: HotelMapModel;
  markers: MapMarker[];
  markerSelected: boolean;
  onClusterDraw?: () => void;
  onClusterSelect?: () => void;
  onMapClick?: () => void;
  onMarkersUpdate?: () => void;
  onSelect?: (pin) => void;
  onSingleMarkerUpdate: () => void;
  searchChanged?: boolean;
  shortlist?: ShortlistStore;
  shouldShowHeartIcon?: boolean;
  singleMarkerChanged?: boolean;
  withClustering?: boolean;
  propertySearchListings?: PropertySearchListingViewModel[];
}

export type LocalizedClusteringMarkersProps = LocalizedComponentProps & ClusteringMarkersProps;

@withLocalization
@inject('shortlist')
@observer
export class ClusteringMarkers extends React.Component<LocalizedClusteringMarkersProps> {
  // set all color values once in constructor, fallback values for older browsers without css vars
  private markerClusterer;

  public constructor(props) {
    super(props);

    this.addEventsToMarkerClusterer = this.addEventsToMarkerClusterer.bind(this);
    this.createGoogleMapsMarker = this.createGoogleMapsMarker.bind(this);
    this.createMarkerClusterer = this.createMarkerClusterer.bind(this);
    this.createPin = this.createPin.bind(this);
    this.getUpdatedMarkerIndex = this.getUpdatedMarkerIndex.bind(this);
    this.hasHeartIcon = this.hasHeartIcon.bind(this);
    this.updateSingleMarker = this.updateSingleMarker.bind(this);
    this.filterFavoritedShortlistModels = this.filterFavoritedShortlistModels.bind(this);
    this.interactedWithFavoriteLocally = this.interactedWithFavoriteLocally.bind(this);
  }

  public shouldComponentUpdate(nextProps) {
    return (
      nextProps.map !== this.props.map || nextProps.google !== this.props.google || nextProps.searchChanged || nextProps.singleMarkerChanged
    );
  }

  public render() {
    const { google, map, onMapClick } = this.props;
    if (map && google) {
      if (!this.markerClusterer || (this.props.searchChanged && !this.props.singleMarkerChanged)) {
        this.props.markers.filter(x => this.isSoldOut(x)).map((marker, index) => this.createGoogleMapsMarker(marker, index));
        const availMarkers = this.props.markers
          .filter(marker => !this.isSoldOut(marker) && !this.isAirport(marker) && !this.isLandmark(marker))
          .map((marker, index) => this.createGoogleMapsMarker(marker, index));
        this.createMarkerClusterer(availMarkers);
        this.addEventsToMarkerClusterer(this.markerClusterer);
        this.props.markers
          .filter(marker => this.isLandmark(marker) || this.isAirport(marker))
          .map((marker, index) => this.createGoogleMapsMarker(marker, index));
      } else if (this.props.singleMarkerChanged) {
        this.updateSingleMarker(this.getUpdatedMarkerIndex());
      }
      if (google && google.maps && google.maps.event) {
        google.maps.event.addDomListener(map, 'mouseup', () => {
          if (this.props.markerSelected) {
            if (typeof onMapClick === 'function') {
              onMapClick();
            }
          }
        });
      }
    }

    return null;
  }

  private createMarkerClusterer(markers) {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
    const { map, google, l10n } = this.props;
    const markerClusterer = MarkerClusterer(google);
    const markerClustererProps = {
      gridSize: 40,
      minimumClusterSize: 10,
      averageCenter: true,
      maxZoom: 21,
      markerLabel: (count, price, isSoldOut) => {
        if (isSoldOut) {
          return l10n.formatText('maps.markers.cluster.soldOut.label', count);
        }

        return l10n.formatText('maps.markers.cluster.available.label', count, price);
      },
    };
    this.markerClusterer = new markerClusterer(map, markers, markerClustererProps);
    this.props.onMarkersUpdate();
  }

  private addEventsToMarkerClusterer(markerClusterer) {
    const { google, onClusterDraw, onClusterSelect } = this.props;
    markerClusterer.addListener('clusterclick', () => {
      onClusterSelect();
    });

    if (google.maps && google.maps.event) {
      google.maps.event.addListenerOnce(markerClusterer, 'drawcluster', () => {
        onClusterDraw();
      });
    }
  }

  private createGoogleMapsMarker(marker, index, isSelected?) {
    const { google, onSelect } = this.props;
    const hasHeartIcon = this.hasHeartIcon(marker);
    const pin = this.createPin(marker, hasHeartIcon, index);

    if (marker.sheet || marker.isActionable) {
      pin.marker.addListener('click', () => {
        onSelect(pin);
      });
      if (google.maps && google.maps.event) {
        google.maps.event.addDomListener(pin.marker.anchor, 'click', () => {
          onSelect(pin);
        });
      }
      if (isSelected) {
        onSelect(pin);
      }
    }

    pin.marker.hotel = marker.sheet ? marker.sheet : marker.hotel;
    pin.marker.hotelId = marker.sheet ? marker.sheet.id : marker.hotel.hotelId;
    pin.marker.isFavorited = hasHeartIcon;
    pin.marker.isSoldOut = this.isSoldOut(marker);
    pin.marker.label = { text: marker.label };
    pin.marker.position = pin.marker.getPosition();

    return pin.marker;
  }

  private isLandmark(marker): boolean {
    return marker.icon && marker.icon === LANDMARK;
  }

  private isAirport(marker): boolean {
    return marker.icon && marker.icon === AIRPORT;
  }

  /**
   * Returns true if the marker describes a sold-out hotel, false otherwise.
   * @param marker {Object}
   */
  private isSoldOut(marker): boolean {
    if (marker.icon && marker.icon === SOLD_OUT_ICON) {
      return true;
    }

    return marker.isSoldOut;
  }

  private createPin(marker, hasHeartIcon, zIndex) {
    const { google, map } = this.props;
    const pinProps = {
      hasHeartIcon,
      zIndex,
      google,
      map,
      position: marker.position,
      hotel: marker.hotel,
      sheet: marker.sheet,
    };
    const isSoldOut = this.isSoldOut(marker);

    if (isSoldOut) {
      return new SoldOutMarker(pinProps);
    }
    const isLandmark = this.isLandmark(marker);
    const isAirport = this.isAirport(marker);
    const markerProps = {
      ...pinProps,
      label: marker.label,
    };

    if (isLandmark) {
      return new PlaceMarker(markerProps);
    } else if (isAirport) {
      return new AirportMarker(markerProps);
    }

    return new PriceMarker(markerProps);
  }

  private filterFavoritedShortlistModels(propertySearchListings) {
    return propertySearchListings && propertySearchListings.filter(el => el?.model?.shortlistModel?.favorited);
  }

  private interactedWithFavoriteLocally(shortlistedLocally) {
    return typeof shortlistedLocally === 'boolean';
  }

  private hasHeartIcon(marker) {
    const { shouldShowHeartIcon, shortlist, propertySearchListings } = this.props;
    const favoritedShortlistModels = this.filterFavoritedShortlistModels(propertySearchListings);
    const shortlisted = favoritedShortlistModels.find(e => e.model.id === (marker.sheet ? marker.sheet.id : marker.hotel.id));
    const shortlistedLocally =
      shortlist.hotel &&
      shortlist.hotel.favoritedLocally &&
      shortlist.hotel.favoritedLocally[marker.sheet ? marker.sheet.id : marker.hotel.id];

    // if shortlisted locally is a Boolean, that means it has been clicked and the local value should supercede the initial API value
    return this.interactedWithFavoriteLocally(shortlistedLocally)
      ? shouldShowHeartIcon && shortlistedLocally
      : shouldShowHeartIcon && shortlisted;
  }

  private getUpdatedMarkerIndex() {
    const { hotelIdFromMap } = this.props;

    return hotelIdFromMap && this.markerClusterer && this.markerClusterer.markers_ && this.markerClusterer.markers_.length > 0
      ? this.markerClusterer.markers_.findIndex(h => (h.sheet ? h.sheet.id : h.hotelId) === hotelIdFromMap)
      : -1;
  }

  private updateSingleMarker(index) {
    const { markers, shortlist } = this.props;

    if (index != null && markers[index]) {
      const updatedMarker = markers && this.createGoogleMapsMarker(markers[index], index, true);
      updatedMarker.isFavorited = !updatedMarker.isFavorited;
      if (this.markerClusterer) {
        this.markerClusterer.updateMarker(updatedMarker, index);
      }

      shortlist.resetHotelIdFromMap();
      this.props.onSingleMarkerUpdate();
    }
  }
}
